import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect } from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { EditAutomationTemplate } from '.';
import { useImpersonationContext } from '../../../hooks/useImpersonationContext';
import { MastheadPortalId } from '../../../models/AdminModels';
import { EditAutomationTemplateStepsContext, IEditAutomationTemplateStepsContext } from '../../../models/Automations';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { isCanvasOnlyAutomationTemplate } from '../../../models/utils/automationTemplateEditorUtils';
import { useAdminAccountQuery, useAutomationTemplateIdQuery } from '../../../queries';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../components/Breadcrumb';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { baseStyleSheet } from '../../styles/styles';

export function AdminAccountEditAutomationTemplate() {
	const params = useParams<{ accountId: string; id: string }>();
	const userSession = useUserSession();
	const impersonationContext = useImpersonationContext({ accountId: params.accountId });
	const accountQuery = useAdminAccountQuery({ accountId: params.accountId });
	const automationTemplateQuery = useAutomationTemplateIdQuery({ id: params.id, impersonationContext });
	const companyName = accountQuery.data?.companyName;
	const automationTemplateName = automationTemplateQuery.data?.name;
	const history = useHistory();

	useEffect(() => {
		if (isCanvasOnlyAutomationTemplate(automationTemplateQuery.data)) {
			history.replace(`/accounts/${params.accountId}/automations/${params.id}/canvas`);
		}
	}, [automationTemplateQuery.data, history, params.accountId, params.id]);

	// Set global impersonation context on the user session
	useEffect(() => {
		if (impersonationContext && userSession) {
			userSession.impersonate(impersonationContext);
		}
		return () => {
			if (userSession) {
				userSession.impersonate(null);
			}
		};
	}, [impersonationContext, userSession]);

	const [unsavedStep, setUnsavedStep] = React.useState<Api.IAutomationStep>(null);
	const editAutomationTemplateStepsContext = React.useMemo<IEditAutomationTemplateStepsContext>(() => {
		return {
			automationTemplateId: params.id,
			impersonationContext,
			unsavedStep,
			setUnsavedStep,
			isAdminContext: true,
		};
	}, [params.id, impersonationContext, unsavedStep]);

	return (
		<>
			<EditAutomationTemplateStepsContext.Provider value={editAutomationTemplateStepsContext}>
				<MultiContainerHeader
					appBarHeader={
						<div className={css(baseStyleSheet.horizontalStack)} style={{ alignItems: 'center', gap: '16px' }}>
							<Breadcrumb>
								<BreadcrumbList>
									<BreadcrumbItem>
										<BreadcrumbLink asChild>
											<NavLink to='/accounts'>Accounts</NavLink>
										</BreadcrumbLink>
									</BreadcrumbItem>
									<BreadcrumbSeparator />
									<BreadcrumbItem>
										<BreadcrumbLink asChild>
											<NavLink to={`/accounts/${params.accountId}?tab=Automations`}>
												{companyName ?? 'Loading...'}
											</NavLink>
										</BreadcrumbLink>
									</BreadcrumbItem>
									<BreadcrumbSeparator />
									<BreadcrumbItem>
										<BreadcrumbPage>{`Automation "${automationTemplateName ?? '...'}"`}</BreadcrumbPage>
									</BreadcrumbItem>
								</BreadcrumbList>
							</Breadcrumb>
							<NavLink
								to={`/accounts/${params.accountId}/automations/${params.id}/test`}
								className={css(baseStyleSheet.brandLink)}
							>
								Preview
							</NavLink>
						</div>
					}
					appBarPortalDestinationId={MastheadPortalId}
				/>
				<EditAutomationTemplate
					impersonationContext={impersonationContext}
					onAutomationDeleted={() => history.replace(`/accounts/${params.accountId}?tab=Automations`)}
					onAutomationPublished={() => history.push(`/accounts/${params.accountId}?tab=Automations`)}
					onAutomationDisabled={() => history.push(`/accounts/${params.accountId}?tab=Automations`)}
				/>
			</EditAutomationTemplateStepsContext.Provider>
		</>
	);
}
